import React, { useState } from 'react';
import { HiOutlineBugAnt, HiOutlineLightBulb, HiOutlineCheckCircle } from "react-icons/hi2";
import axios from '../Actions/axios.config';
import { trackFormSubmit } from '../Actions/analytics';

const HelpPage = () => {
  const [activeTab, setActiveTab] = useState('bug'); // 'bug' or 'suggestion'
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({ title: '', description: '', severity: 'Low' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // We send the 'type' (bug/suggestion) so the backend knows which collection to use
      await axios.post('user/feedback/submit', { ...formData, category: activeTab });
      trackFormSubmit(activeTab === 'bug' ? 'bug_report' : 'suggestion');
      setSubmitted(true);
      setFormData({ title: '', description: '', severity: 'Low' }); // Reset form
    } catch (err) {
      alert("Failed to send. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center p-6 text-center">
        <HiOutlineCheckCircle size={80} className="text-green-500 mb-4 animate-bounce" />
        <h2 className="text-3xl font-black text-slate-900">Thanks for helping out!</h2>
        <p className="text-slate-500 mt-2 max-w-sm">Our team will look into this {activeTab} ASAP. You're making CampusIRA better for everyone.</p>
        <button onClick={() => setSubmitted(false)} className="mt-8 font-bold text-blue-600 hover:underline">Send another report</button>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-6 py-12 lg:py-20 font-sans">
      <header className="text-center mb-12">
        <h1 className="text-4xl font-black text-slate-900 tracking-tight mb-3">Help us Improve</h1>
        <p className="text-slate-500 font-medium">Found a glitch or have a brilliant idea? We're all ears.</p>
      </header>

      {/* Tab Switcher */}
      <div className="flex p-1 bg-slate-100 rounded-2xl mb-10">
        <button 
          onClick={() => { setActiveTab('bug'); setFormData({title:'', description:'', severity:'Low'}) }}
          className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-bold transition-all ${activeTab === 'bug' ? 'bg-white text-red-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
        >
          <HiOutlineBugAnt /> Report a Bug
        </button>
        <button 
          onClick={() => { setActiveTab('suggestion'); setFormData({title:'', description:''}) }}
          className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-bold transition-all ${activeTab === 'suggestion' ? 'bg-white text-amber-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
        >
          <HiOutlineLightBulb /> Suggestion
        </button>
      </div>

      {/* Form Section */}
      <form onSubmit={handleSubmit} className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
        <div>
          <label className="block text-xs font-black uppercase tracking-widest text-slate-400 mb-2">Subject</label>
          <input 
            required
            type="text" 
            placeholder={activeTab === 'bug' ? "What went wrong?" : "What's your idea?"}
            className="w-full px-5 py-4 bg-slate-50 border-2 border-slate-100 rounded-2xl focus:border-blue-500 focus:bg-white outline-none transition-all font-bold"
            value={formData.title}
            onChange={(e) => setFormData({...formData, title: e.target.value})}
          />
        </div>

        {activeTab === 'bug' && (
          <div>
            <label className="block text-xs font-black uppercase tracking-widest text-slate-400 mb-2">Severity</label>
            <select 
              className="w-full px-5 py-4 bg-slate-50 border-2 border-slate-100 rounded-2xl outline-none font-bold text-slate-700"
              value={formData.severity}
              onChange={(e) => setFormData({...formData, severity: e.target.value})}
            >
              <option value="Low">Low (Typo, Layout)</option>
              <option value="Medium">Medium (Button not working)</option>
              <option value="High">High (Site crash, Login issue)</option>
            </select>
          </div>
        )}

        <div>
          <label className="block text-xs font-black uppercase tracking-widest text-slate-400 mb-2">Description</label>
          <textarea 
            required
            rows="5"
            placeholder={activeTab === 'bug' ? "Tell us exactly how it happened..." : "Describe the feature and how it would help students..."}
            className="w-full px-5 py-4 bg-slate-50 border-2 border-slate-100 rounded-2xl focus:border-blue-500 focus:bg-white outline-none transition-all font-medium leading-relaxed"
            value={formData.description}
            onChange={(e) => setFormData({...formData, description: e.target.value})}
          />
        </div>

        <button 
          disabled={loading}
          type="submit" 
          className={`w-full py-5 rounded-2xl font-black text-white shadow-lg transition-all active:scale-95 flex items-center justify-center gap-2 ${activeTab === 'bug' ? 'bg-red-500 hover:bg-red-600 shadow-red-100' : 'bg-amber-500 hover:bg-amber-600 shadow-amber-100'}`}
        >
          {loading ? 'Sending...' : `Submit ${activeTab === 'bug' ? 'Bug Report' : 'Suggestion'}`}
        </button>
      </form>
    </div>
  );
};

export default HelpPage;